/** Hàm phụ dùng chung của màn Phòng Kinh doanh (tách khỏi tệp linh kiện). */
import type { SalesOrgView, SalesTeamPerson, SalesTeamSummary } from "./api";
import type { SalesTeamTab } from "./SalesTeamView";

/** Tiểu đội còn hoạt động ít nhất một ngày trong tháng. */
export function squadsOfMonth(org: SalesOrgView, month: string) {
  const first = `${month}-01`;
  const last = `${month}-31`;
  return org.units
    .filter((u) => u.kind === "squad" && u.startDate <= last && (u.endDate === "" || u.endDate >= first))
    .sort((a, b) => a.name.localeCompare(b.name, "vi"));
}

/**
 * Tiểu đội hiện ô nhập ở thẻ Mục tiêu / Chất lượng (CEO 2a, 31/10/2026): bỏ tiểu
 * đội cả tháng không có ai — vd bản cũ HRM đóng khi chuyển tiểu đội sang đội
 * khác, trùng tên với bản mới. Tiểu đội đã có số lưu (`saved`) vẫn hiện, để
 * không ai mất số đã nhập mà không thấy.
 */
export function squadsToEnter(org: SalesOrgView, month: string, saved: ReadonlySet<string>) {
  const first = `${month}-01`;
  const last = `${month}-31`;
  const hasPeople = (squadId: string) =>
    org.members.some((m) => m.squadId === squadId && m.startDate <= last && (m.endDate === "" || m.endDate >= first));
  return squadsOfMonth(org, month).filter((u) => saved.has(u.id) || hasPeople(u.id));
}

/**
 * Cộng các khoản theo doanh số của một người — gương của
 * `salesteam.PersonResult.Variable()` (máy chủ). Lương cứng để riêng: phiếu
 * lương chia tiếp theo ngày công.
 */
export function variableOf(p: SalesTeamPerson): number {
  return (
    p.commission80 +
    p.bonus80 +
    p.pocketShare +
    p.squadLeadTier +
    p.teamLeadBonus +
    p.regionLeadBonus +
    p.quarterTopUp +
    p.reversal80
  );
}

export const PERSON_COLS: { key: keyof SalesTeamPerson; label: string }[] = [
  { key: "commission80", label: "Hoa hồng tự bán" },
  { key: "bonus80", label: "Thưởng vượt mục tiêu" },
  { key: "pocketShare", label: "Chia túi tiểu đội" },
  { key: "squadLeadTier", label: "Thưởng tiểu đội trưởng" },
  { key: "teamLeadBonus", label: "Thưởng đội trưởng" },
  { key: "regionLeadBonus", label: "Thưởng leader miền" },
  // Chỉ tháng đầu quý sau (01, 04, 07, 10) mới có — QĐ 0483 mục 3.7.
  { key: "quarterTopUp", label: "Bù thưởng vượt theo quý" },
  { key: "reversal80", label: "Trừ huỷ / hoàn" },
];

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Tệp CSV cho kế toán: mỗi người một dòng, đúng các cột trên màn. Cột bù quý chỉ khi có người được bù. */
export function summaryCsv(s: SalesTeamSummary): string {
  const quarter = s.people.some((p) => p.quarterTopUp !== 0);
  const cols = PERSON_COLS.filter((c) => c.key !== "quarterTopUp" || quarter);
  const head = [
    "Mã nhân sự",
    "Họ tên",
    ...(s.baseSalaryHidden ? [] : ["Lương cứng đủ tháng"]),
    ...cols.map((c) => c.label),
    "Cộng khoản theo doanh số",
  ];
  const rows = s.people.map((p) => [
    p.staffId,
    p.name,
    ...(s.baseSalaryHidden ? [] : [p.baseFull]),
    ...cols.map((c) => p[c.key] as number),
    variableOf(p),
  ]);
  return [head, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
}

/** Ô chọn người ghi công lọc theo loại thành viên Phòng KD; "any" cho ô sale chủ. */
export type PickKind = "sales" | "cskh" | "any";
export type PickPerson = { staffId: string; name: string };
export type PickGroup = { squadId: string; name: string; people: PickPerson[] };

/**
 * Người chọn được ở ô ghi công (CEO 2a, 30/10/2026), xếp theo tiểu đội của
 * tháng: đúng loại, có hồ sơ GNPF (mã tạm "hrm:" chưa ghi công được), ở tiểu
 * đội ít nhất một ngày trong tháng, mỗi người một lần mỗi tiểu đội. Tiểu đội
 * ưu tiên lên đầu; trong tiểu đội xếp theo tên.
 */
export function pickGroups(org: SalesOrgView, month: string, kind: PickKind, prefer: readonly string[]): PickGroup[] {
  const first = `${month}-01`;
  const last = `${month}-31`;
  const groups = squadsOfMonth(org, month).map((u) => {
    const seen = new Set<string>();
    const people: PickPerson[] = [];
    for (const m of org.members) {
      if (m.squadId !== u.id || m.staffId.startsWith("hrm:") || seen.has(m.staffId)) continue;
      if (kind !== "any" && m.kind !== kind) continue;
      if (m.startDate > last || (m.endDate !== "" && m.endDate < first)) continue;
      seen.add(m.staffId);
      people.push({ staffId: m.staffId, name: m.staffName || m.staffId });
    }
    people.sort((a, b) => a.name.localeCompare(b.name, "vi"));
    return { squadId: u.id, name: u.name, people };
  });
  const rank = (g: PickGroup) => (prefer.includes(g.squadId) ? 0 : 1);
  return groups.filter((g) => g.people.length > 0).sort((a, b) => rank(a) - rank(b));
}

type Warning = SalesTeamSummary["warnings"][number];

/** Nhóm việc chặn chốt trên Bảng tổng tháng (CEO 1a, 30/10/2026) và thẻ phải sang để xử lý. */
export const WARN_GROUPS = [
  { id: "quality", label: "Tiêu chí chất lượng", tab: "quality" },
  { id: "target", label: "Mục tiêu tháng", tab: "targets" },
  { id: "credit", label: "Ghi công đơn", tab: "pending" },
  { id: "gnpm", label: "Đơn GNPM", tab: "pending" },
  { id: "structure", label: "Cơ cấu HRM", tab: "structure" },
  { id: "other", label: "Việc khác", tab: null },
] as const satisfies readonly { id: string; label: string; tab: SalesTeamTab | null }[];

export type WarnGroupId = (typeof WARN_GROUPS)[number]["id"];

/** Mã cảnh báo của máy chủ (`salesteam.Warning.Code`) ⇒ nhóm. Mã lạ rơi vào "Việc khác", không mất. */
export function warnGroupOf(code: string): WarnGroupId {
  if (code === "missing_on_time" || code === "missing_satisfied") return "quality";
  if (code === "missing_target") return "target";
  if (code.startsWith("gnpm_")) return "gnpm";
  if (code.startsWith("credit_") || code === "upsell_no_owner") return "credit";
  if (code.startsWith("structure_")) return "structure";
  return "other";
}

export type WarnGroup = (typeof WARN_GROUPS)[number] & { items: Warning[] };

/** Gom cảnh báo theo nhóm, giữ thứ tự nhóm ở WARN_GROUPS và thứ tự máy chủ trong nhóm. */
export function groupWarnings(warnings: readonly Warning[]): WarnGroup[] {
  return WARN_GROUPS.map((g) => ({ ...g, items: warnings.filter((w) => warnGroupOf(w.code) === g.id) })).filter(
    (g) => g.items.length > 0,
  );
}

/** Tên tiêu chí trong lời máy chủ «…» — để gộp nhiều tiêu chí của một tiểu đội vào một dòng. */
export function criterionOf(message: string): string {
  return /«([^»]+)»/.exec(message)?.[1] ?? message;
}

/** Cảnh báo có tiểu đội gom theo tiểu đội (giữ thứ tự gặp đầu); cảnh báo không có tiểu đội để riêng. */
export function bySquad(items: readonly Warning[]): { squads: { squadId: string; items: Warning[] }[]; rest: Warning[] } {
  const squads: { squadId: string; items: Warning[] }[] = [];
  const rest: Warning[] = [];
  for (const w of items) {
    if (!w.squadId) {
      rest.push(w);
      continue;
    }
    const hit = squads.find((s) => s.squadId === w.squadId);
    if (hit) hit.items.push(w);
    else squads.push({ squadId: w.squadId, items: [w] });
  }
  return { squads, rest };
}
